import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CountryChart } from '../country-chart';
import { Country } from '../country';
import { CountriesService } from '../countries.service';
import { ShiftHolderChart } from '../shift-holder-chart';
import { SiteCountry, SiteTotalService } from '../site-total.service';
import { forkJoin } from 'rxjs';
import { ThemeService } from '../theme.service';

@Component({
  selector: 'app-countries',
  templateUrl: './countries.component.html',
  styleUrls: ['./countries.component.scss']
})
export class CountriesComponent implements OnInit, OnDestroy {

  countryItems: Country[] = [];
  countryChart: CountryChart[] = [];
  country = 'ALL';
  // numCols: any = 3;
  currDate: Date = new Date();
  refreshTimer: any;
  // rowHeight: any = 400;

  // key: site ID, value: array of SiteCountry type
  sitesCountries: Record<string, SiteCountry[]> = {};
  // key: site ID, value: inspector IDs
  futureSiteInspectors: Record<number, number[]> = {};
  // key: site ID, value: positions
  positions: Record<number, any[]> = {};

  constructor(
    private route: ActivatedRoute,
    private countriesService: CountriesService,
    private sitesTotalService: SiteTotalService,
    public themeService: ThemeService,
    private location: Location,
    private router: Router
  ) {
    // this.route.params.subscribe(event => {
    //   this.ngOnInit();
    // });
  }

  ngOnInit() {
    if (localStorage.getItem('token') === null) {
      this.router.navigate(['/login']);
    }
    if (localStorage.getItem('selectedDate') !== null) {
      this.currDate = new Date(localStorage.getItem('selectedDate'));
    } else {
      localStorage.setItem('selectedDate', this.getFormattedDate(this.currDate));
    }
    localStorage.removeItem('country');
    localStorage.removeItem('branch');
    localStorage.removeItem('siteId');
    // this.adjustSize();

    this.getCountryData(this.country);
    this.getCountriesSites();
    this.refreshTimer = setInterval(() => {
      this.getCountryData(this.country);
      this.getCountriesSites();
    }, 1000 * 60 * 10);
  }

  ngOnDestroy() {
    clearInterval(this.refreshTimer);
  }

  getCountriesSites(): void {
    // Clear data
    this.sitesCountries = {};
    this.futureSiteInspectors = {};
    this.positions = {};

    const selectedDate = new Date(this.currDate);
    selectedDate.setDate(selectedDate.getDate() + 1);

    const currentDate = this.getFormattedDate(this.currDate);
    const nextDayDate = this.getFormattedDate(selectedDate);

    // forkJoin is like Promise.all, but it's used in Angular instead
    forkJoin([
      this.sitesTotalService.getSitesAssignments(currentDate),
      this.sitesTotalService.getSitesPositions(currentDate),
      this.sitesTotalService.getFutureInspectorIds(nextDayDate),
      this.sitesTotalService.getSitesCountries(currentDate)
    ]).subscribe(([currentSites, currentPositions, futureInspectors, countriesSites]) => {

      const localFutureInspectors: Record<number, number[]> = {};

      futureInspectors.forEach(assignment => {
        // Fill futureSiteInspectors key with the assignment site ID and fill the value with its inspector IDs
        localFutureInspectors[assignment.site] = assignment.inspectorIds.split(',').map(inspectorId => Number(inspectorId));
      });

      this.futureSiteInspectors = localFutureInspectors;

      // sites will hold records of site IDs as a key and site assignments as a value
      const sites: Record<number, any[]> = {};

      currentSites.forEach(assignment => {
        // If key exists then push the data
        if (sites[assignment.site]) {
          sites[assignment.site].push(assignment);
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          sites[assignment.site] = [assignment];
        }
      });

      const localPositions: Record<number, any[]> = {};

      // Same process as the top currentSites
      currentPositions.forEach(position => {
        // If key exists then push the data
        if (localPositions[position.site]) {
          localPositions[position.site].push(position);
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          localPositions[position.site] = [position];
        }
      });

      this.positions = localPositions;

      const localSitesCountries: Record<string, SiteCountry[]> = {};

      countriesSites.forEach(countryAssignment => {
        const countrySites = {
          ...countryAssignment,
          sites: sites[countryAssignment.siteId] // Get sites value based on the site ID
        };

        // If key exists then push the data
        if (localSitesCountries[countryAssignment.country]) {
          // Sometimes countries have the same siteId so we gotta make sure we don't push those records if they already exist
          if (!localSitesCountries[countryAssignment.country].some(({ siteId }) => siteId === countryAssignment.siteId)) {
            localSitesCountries[countryAssignment.country].push(countrySites);
          }
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          localSitesCountries[countryAssignment.country] = [countrySites];
        }

        // Add "ALL" option to sitesCountries that will include everything
        if (localSitesCountries['ALL']) {
          // Same condition up there that checks for duplicated site IDs
          if (!localSitesCountries['ALL'].some(({ siteId }) => siteId === countryAssignment.siteId)) {
            localSitesCountries['ALL'].push(countrySites);
          }
        } else {
          localSitesCountries['ALL'] = [countrySites];
        }
      });

      this.sitesCountries = localSitesCountries;
    });
  }

  getCountryData( country: string ): void {
    this.countriesService.getCountriesData(this.country, this.getFormattedDate(this.currDate))
      .subscribe((data) => {
        this.compileCountriesData(data);
      });
  }

  changeDate( t: any ): void {
    this.currDate = t.value;
    localStorage.setItem('selectedDate', t.value);
    this.getCountryData(this.country);
    this.getCountriesSites();
  }

  getFormattedDate( dt: Date ): string {
    let m: any = dt.getMonth() + 1;
    m = (m < 10) ? '0' + m.toString() : m.toString();
    let d: any = dt.getDate();
    d = (d < 10) ? '0' + d.toString() : d.toString();

    return dt.getFullYear().toString() + '-' + m.toString() + '-' + d.toString();
  }

  compileCountriesData( data: any ) {
    this.countryChart = [];

    if (data.length === 0) {
      return;
    }

    let tempName = '';
    let tempCountryChart: CountryChart = new CountryChart();
    let shiftItem: ShiftHolderChart = new ShiftHolderChart();
    let tempHit = 0;
    let tempTotal = 0;
    let tempFilled = 0;
    const tempData = JSON.parse(JSON.stringify(data));

    // Populate values for ALL item
    tempName = 'ALL';
    tempCountryChart.countryName = 'ALL';
    // Sort by shift, then filled
    tempData.sort(function (a, b) {
      const aConcat = a.shift + a.isFilled;
      const bConcat = b.shift + b.isFilled;

      if (aConcat > bConcat) {
        return 1;
      } else if (aConcat < bConcat) {
        return -1;
      } else {
        return 0;
      }
    });

    // Temporary method to seperate shift totals
    let tempShift = '';
    const tempTotalCount = new Array();
    for (const item of tempData) {
      if (tempShift === '') {
        tempShift = item.shift;
        tempFilled = item.isFilled;
        tempTotalCount[item.shift + item.isFilled] = item.qty;
      } else {
        if (tempShift === item.shift && tempFilled === item.isFilled) {
          tempTotalCount[item.shift + item.isFilled] += item.qty;
        } else {
          tempShift = item.shift;
          tempFilled = item.isFilled;
          tempTotalCount[item.shift + item.isFilled] = item.qty;
        }
      }
    }

    for (const item of tempData) {
      if (tempName === '') {
        tempName = 'ALL';
        tempCountryChart.countryName = 'ALL';
        tempCountryChart.shifts.push(item);
        tempTotal += parseFloat(item.qty);
        if (item.isFilled === 1) {
          tempFilled += parseFloat(item.qty);
        }
      } else {
        for (const shift of tempCountryChart.shifts) {
          if (shift.shift === item.shift && shift.isFilled === item.isFilled) {
            tempHit = 1;
            shift.qty += item.qty;
          }
        }
        if (tempHit === 1) {
          tempHit = 0;
        } else {
          tempCountryChart.shifts.push(item);
        }
        tempTotal += parseFloat(item.qty);
        if (item.isFilled === 1) {
          tempFilled += parseFloat(item.qty);
        }
      }
    }
    this.countryItems = data;
    tempCountryChart.total = tempTotal;
    tempCountryChart.filled = tempFilled;
    this.countryChart.push(tempCountryChart);

    // Populate values for Country items
    tempName = '';
    tempCountryChart = new CountryChart();
    shiftItem = new ShiftHolderChart();
    tempHit = 0;
    tempTotal = 0;
    tempFilled = 0;

    for (const item of data) {
      if (tempName === '') {
        tempName = item.country;
        tempCountryChart.countryName = item.country;
        tempCountryChart.shifts.push(item);
        tempTotal += parseFloat(item.qty);
        if (item.isFilled === 1) {
          tempFilled += parseFloat(item.qty);
        }
      } else {
        if (tempName === item.country) {
          tempCountryChart.shifts.push(item);
          tempTotal += parseFloat(item.qty);
          if (item.isFilled === 1) {
            tempFilled += parseFloat(item.qty);
          }
        } else {
          tempCountryChart.total = tempTotal;
          tempCountryChart.filled = tempFilled;
          this.countryChart.push(tempCountryChart);
          tempCountryChart = new CountryChart();
          tempTotal = 0;
          tempFilled = 0;
          tempName = item.country;
          tempCountryChart.countryName = item.country;
          tempCountryChart.shifts.push(item);
          tempTotal += parseFloat(item.qty);
          if (item.isFilled === 1) {
            tempFilled += parseFloat(item.qty);
          }
        }
      }
    }
    this.countryItems = data;
    tempCountryChart.total = tempTotal;
    tempCountryChart.filled = tempFilled;
    this.countryChart.push(tempCountryChart);

    tempName = '';
    tempTotal = 0;
    let shiftTotals = [];
    for (const country of this.countryChart) {
      shiftTotals = [];
      let tempOb = [];
      for (const item of country.shifts) {
        if (item.shift === tempName) {
          tempTotal += item.qty;
        } else {
          tempOb = [tempName, tempTotal];
          shiftTotals.push(tempOb);
          tempName = item.shift;
          tempTotal = item.qty;
        }
      }
      tempOb = [tempName, tempTotal];
      shiftTotals.push(tempOb);
      for (const item of country.shifts) {
        for (const shiftTotal of shiftTotals) {
          if (item.shift === shiftTotal[0]) {
            item.shiftTotal = shiftTotal[1];
          }
        }
      }
    }
    this.sortCountries();
  }

  goBack(): void {
    this.location.back();
  }

  goBranch(item: any) {
    this.router.navigate(['branches/' + item]);
  }

  // onResize( event ) {
  //   const element = event.target.innerWidth;
  //   if (element < 535) {
  //     this.rowHeight = '325px;';
  //     this.numCols = 1;
  //   } else if (element < 1100) {
  //     this.numCols = 1;
  //     this.rowHeight = '500px;';
  //   } else if (element < 1600) {
  //     this.numCols = 2;
  //   } else {
  //     this.numCols = 3;
  //   }
  // }

  // adjustSize(  ) {
  //   const element = window.innerWidth;
  //   if (element < 535) {
  //     this.rowHeight = '325px;';
  //     this.numCols = 1;
  //   } else if (element < 1100) {
  //     this.rowHeight = '500px;';
  //     this.numCols = 1;
  //   } else if (element < 1600) {
  //     this.numCols = 2;
  //   } else {
  //     this.numCols = 3;
  //   }
  // }

  filter( data: any[] ): any[] {
    const sc: CountryChart[] = [];
    data.forEach(function(d) {
      if (d.countryName.toUpperCase().indexOf((<HTMLInputElement>document.getElementById('search')).value.toUpperCase()) !== -1) {
        sc.push(d);
      }
    });
    return sc;
  }

  sortCountries() {
    this.countryChart.sort((a, b) => {
      if (a.total === 0 && b.total === 0) { // Both totals are 0, so seperate sort for alpha
        if (a.countryName < b.countryName) {
          return -1;
        } else {
          return 1;
        }
      } else if (a.total === 0) { // Only a is 0, send to end
        return 1;
      } else if (b.total === 0) { // Only b is 0, send to end
        return -1;
      } else { // Alpha sort, both items have values
        if (a.countryName < b.countryName) {
          return -1;
        } else {
          return 1;
        }
      }
    });
  }

}
